import { WebhookSigningStatus } from "../schema/webhookSigning";
import { formatWebhookDay } from "./describeWebhookHealth";

/**
 * What the Signing column says (PREC-4249), from the API's `webhookSigningStatus`: "Signed" once
 * the secret was shown, "Secret not yet viewed" for one nobody has seen (a webhook created before
 * signing), and while a rotation overlaps, until when the old secret still signs.
 */
export interface WebhookSigningDescription {
	state: "signed" | "notViewed";
	/** i18n key and params of each line under the badge. */
	lines: { key: string; params?: Record<string, string> }[];
	revealable: boolean;
}

/** A moment with its time, for the end of a rotation's overlap: "14 Oct 2026, 10:42". */
export const formatWebhookMoment = (value: string): string =>
	new Date(value).toLocaleString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

export function describeWebhookSigning(
	signing: WebhookSigningStatus | undefined
): WebhookSigningDescription | null {
	if (!signing) {
		return null;
	}

	const lines: WebhookSigningDescription["lines"] = [];
	const created = formatWebhookDay(signing.secretCreatedAt);

	if (signing.previousSecretValidUntil) {
		lines.push({
			key: "webhookSigning.rotatingUntil",
			params: { date: formatWebhookMoment(signing.previousSecretValidUntil) },
		});
	} else if (signing.state === "not-viewed") {
		lines.push({ key: "webhookSigning.createdBeforeSigning" });
	} else if (created) {
		lines.push({ key: "webhookSigning.secretCreated", params: { date: created } });
	}

	return {
		state: signing.state === "signed" ? "signed" : "notViewed",
		lines,
		revealable: signing.revealable,
	};
}
